import React, { useContext, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { TextField, MaskedTextField } from '@fluentui/react/lib/TextField';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  SiteConfigContext,
  useDebouncedDispatchSiteConfig,
} from '@bingads-webui-campaign-react/smart-page-context';
import {
  getDataSourceByKey,
  sectionTitleStyles,
} from '@bingads-webui-campaign-react/smart-component-util';
import { validateText } from './text-helper';

const defaultStyles = {
  subComponentStyles: {
    label: () => ({
      root: sectionTitleStyles,
    }),
  },
  description: {
    display: 'block',
    textAlign: 'right',
    fontSize: 12,
  },
};

export const SmartTextEditor = (props) => {
  const {
    // below two fields are mutually exclusive (dataSourceName takes precedence)
    dataSourceName, value,

    serializeValue,
    deSerializeValue,
    maxCharCount,
    onChange,
    onFocus,
    focus,
    validation,
    mask,
    prefix,
    customStyles,
    errorMessage,
    isRequired,
    shouldTrimValue,
    ...passThroughProps
  } = props;
  const propsForMask = mask && {
    maskChar: '#',
    mask,
    prefix,
  };
  const styles = {
    ...defaultStyles,
    ...customStyles,
  };

  const i18n = useContext(I18nContext);
  const { siteConfig } = useContext(SiteConfigContext);
  const debouncedDispatchSiteConfig = useDebouncedDispatchSiteConfig();

  const TextInput = mask ? MaskedTextField : TextField;
  // MaskedTextField requires that value needs to be undefined instead of null
  const inputValue = getDataSourceByKey(siteConfig, dataSourceName, value, false);
  const [internalVal, setInternalVal] = useState(inputValue);

  const internalTextInputValue = deSerializeValue(internalVal) || '';
  const textInputValue = deSerializeValue(inputValue) || '';

  const inputRef = useRef(null);

  const validatedResult = useMemo(
    () => validateText(internalTextInputValue, maxCharCount, i18n, validation, isRequired),
    [internalTextInputValue, maxCharCount, i18n, validation, isRequired]
  );

  const onInputChange = useCallback((e, val) => {
    const trimmedFormattedVal = serializeValue(val.trim());
    const formattedVal = serializeValue(val);

    // Internal value never get trimmed, otherwise user could not input space
    setInternalVal(formattedVal);

    if (dataSourceName) {
      debouncedDispatchSiteConfig({
        type: 'UPDATE_DATASOURCE_BY_NAME',
        payload: {
          name: dataSourceName,
          value: shouldTrimValue ? trimmedFormattedVal : formattedVal,
        },
      });
    }
    // Reflect change to outside
    onChange({
      value: shouldTrimValue ? trimmedFormattedVal : formattedVal,
    });
  }, [dataSourceName, debouncedDispatchSiteConfig, onChange, serializeValue, shouldTrimValue]);

  // For those update value outside of this component, force update this value. e.g. location sugs
  // to make the internal value same as the context value.
  useEffect(() => {
    // We compare trimmed value because internal state could have empty spaces
    if (textInputValue.trim() !== internalTextInputValue.trim()) {
      setInternalVal(inputValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  useEffect(() => {
    if (inputRef.current && focus) {
      inputRef.current.focus();
      onFocus();
    }
  }, [focus, onFocus]);

  return (
    <TextInput
      componentRef={(input) => { inputRef.current = input; }}
      styles={styles}
      errorMessage={errorMessage || validatedResult.errorMessage}
      onFocus={onFocus}
      onChange={onInputChange}
      value={internalTextInputValue}
      description={validatedResult.charRemainingDescription}
      {...propsForMask}
      {...passThroughProps}
    />
  );
};

SmartTextEditor.propTypes = {
  dataSourceName: PropTypes.string,
  label: PropTypes.string.isRequired,
  onRenderLabel: PropTypes.func,
  multiline: PropTypes.bool,
  validation: PropTypes.func,
  focus: PropTypes.bool,
  isRequired: PropTypes.bool,
  serializeValue: PropTypes.func,
  deSerializeValue: PropTypes.func,
  mask: PropTypes.string,
  prefix: PropTypes.string,
  errorMessage: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
  ]),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  maxCharCount: PropTypes.number,
  value: PropTypes.string,
  customStyles: PropTypes.shape({}),
  onFocus: PropTypes.func,
  shouldTrimValue: PropTypes.bool,
};

SmartTextEditor.defaultProps = {
  // below two fields are mutually exclusive (dataSourceName takes precedence)
  dataSourceName: null,
  value: undefined,

  // below two fields are mutually exclusive (errorMessage takes precedence)
  errorMessage: undefined,
  validation: undefined,

  isRequired: false,

  // for MaskTextField
  mask: undefined,
  prefix: undefined,

  multiline: false,
  onRenderLabel: undefined,
  focus: false,
  serializeValue: _.identity,
  deSerializeValue: _.identity,
  onChange: _.noop,
  onFocus: _.noop,
  placeholder: undefined,
  maxCharCount: undefined,
  customStyles: {},
  shouldTrimValue: false,
};
